import React, { useState, Fragment } from 'react'
import propTypes from 'prop-types'
import defaultTheme from '../../../resources/themes/default'
import withImmutablePropsToJS from 'with-immutable-props-to-js'
/** Import components */
import { Layout, Content, Header, HeaderBreadCrum } from '../../layouts/AuthenticatedLayout'
import headerConfig from '../../services/resources/ServiceTableConfig'
import filterConfig from '../../services/resources/FilterConfig'

import SideBar from '../../common/DashboadSideBar'
import DashboardCard from '../../dashboard/components/DashboardCard'
import ServicesTables from '../../services/components/ServicesTable'
import Pagination from '../../common/Pagination/Pagination'
import { useDashboardContainer } from '../../../resources/styles/DashboardStyles'

import { useConfigurator } from '../../common/apsys.datagrid.materialui/UseConfigurator'
import Configurator from '../../common/apsys.datagrid.materialui/PopperConfigurator'
import { useVisibleColumns } from '../../../hooks/useVisibleColumns'
import { IconButton } from '@material-ui/core'
import ConfigureIcon from '@material-ui/icons/SettingsOutlined'
import {Typography} from '@material-ui/core'

import {GetStatusClosedServices} from '../../../store/helpers/FilterHelper'

/**
 * Authenticated desktop view
 */
const IndexClosedDesktopView = (props) => {
	/**Functions */
	const {
		onChangeSearchValue,
		redirectToServiceDetail,
		onDownloadReportOpenServices,
		onFastDownloadReportOpenServices
	} = props
	/** Arrays*/
	const {
		serviceProviders,
		salesRepresentatives,
		retails,
		internalUsers
	} = props
	/**String values */
	const { searchValue } = props
	/**Objects */
	const { userProfile } = props
	/**bolean */
	const { isDownloadReport } = props
	const contentClasses = useDashboardContainer()
	/** Table configuration. Shares the storage key with the open-services views, so it has to
	 * agree on the same visible column set — see useVisibleColumns. */
	const visibleHeaderConfig = useVisibleColumns(headerConfig, userProfile)
	const [serviceTableConfiguration, onChangeConfiguration] = useConfigurator(
		'dashboard-services-datagrid-config',
		visibleHeaderConfig
	)
	const [configuratorAnchorEl, setConfigurationAnchorEl] = useState(null)
	/** Filter configuration */
	const [filterConfiguration, onChangeFilterConfiguration] = useConfigurator('closed-services-filter-config', filterConfig)

	/**
	 * The user change the service table configuration
	 */
	const onConfigurationChange = (configuration) => {
		onChangeConfiguration(configuration)
		setConfigurationAnchorEl(null)
	}

	return (
		<Layout>
			<Header 
				key={'AuthenticatedHeader'}
				user={props.user}
				breadcrumbs={
					<HeaderBreadCrum>
						<Typography color='secondary'>Services</Typography>
					</HeaderBreadCrum>
				}
			/>
			<SideBar
				key={'AuthenticatedSideBar'}
				onChangeLocation={props.onChangeLocation}
				selectedButtonIndex={2}
				userProfile={userProfile}
			/>
			<Content key={'AuthenticatedContent'}>
				<div className={contentClasses.root}>
					<div className={contentClasses.cardList}>
						<DashboardCardLists
							isLoadingDashboard={props.isLoadingDashboard}
							dashboard={props.dashboard}
						/>
					</div>
					<div className={contentClasses.container}>
						<ServicesTables
							services={props.services}
							isLoading={props.isLoading}
							onchangeSortingCriteria={
								props.onchangeServiceSortingCriteria
							}
							sortBy={props.sortServiceBy}
							sortDirection={props.sortServiceDirection}
							serviceProviders={serviceProviders}
							salesRepresentatives={salesRepresentatives}
							retails={retails}
							onChangeSearchValue={onChangeSearchValue}
							searchValue={searchValue}
							internalUsers={internalUsers}
							redirectToServiceDetail={redirectToServiceDetail}
							userProfile={userProfile}
                            configuration={serviceTableConfiguration}
                            isServicesOpen={false}
							filterConfiguration={filterConfiguration}
							onFilterConfigurationChange={onChangeFilterConfiguration}
							optionsStatus={GetStatusClosedServices()}
							onDownloadReportOpenServices={onDownloadReportOpenServices}
							onFastDownloadReportOpenServices={onFastDownloadReportOpenServices}
							isDownloadReport={isDownloadReport}
						/>
					</div>
					<div className={contentClasses.pagination}>
						<div className={contentClasses.grow} />
						<Pagination
							count={props.totalServicesPages}
							page={props.servicePageNumber}
							onChangePage={props.onChangeServicePage}
						/>
						<IconButton
							size='small'
							color='primary'
							onClick={(event) =>
								setConfigurationAnchorEl(
									configuratorAnchorEl
										? null
										: event.currentTarget
								)
							}
						>
							<ConfigureIcon size='small' />
						</IconButton>
					</div>
					<Configurator
						open={configuratorAnchorEl ? true : false}
						anchorEl={configuratorAnchorEl}
						configuration={serviceTableConfiguration}
						onConfigurationChange={onConfigurationChange}
						onClickAway={() => setConfigurationAnchorEl(null)}
					/>
				</div>
			</Content>
		</Layout>
	)
}
IndexClosedDesktopView.propTypes = {
	/**
	 * The list of services
	 */
	services: propTypes.arrayOf(
		propTypes.shape({
			Ticket: propTypes.any.isRequired,
			Serie: propTypes.any.isRequired,
			Model: propTypes.any.isRequired
		})
	).isRequired,
	/**
	 * The total service's pages
	 */
	totalServicesPages: propTypes.number.isRequired,
	/**
	 * The actual page number
	 */
	servicePageNumber: propTypes.number.isRequired,
	/**
	 * Callback executed when the user change the service's page
	 */
	onChangeServicePage: propTypes.func,
	/**
	 * On change service sorting criteria
	 */
	onchangeServiceSortingCriteria: propTypes.func,
	/**
	 * Determine the sorting column for the service table
	 */
	sortServiceBy: propTypes.string,
	/**
	 * The sort direction for the service table
	 */
	sortServiceDirection: propTypes.oneOf(['asc', 'desc']),
	/**
	 * Determine if is loading the services list
	 */
	isLoading: propTypes.bool.isRequired,
	/**
	 * Callback executed when the user request a change location
	 */
	onChangeLocation: propTypes.func.isRequired
}
IndexClosedDesktopView.defaultProps = {
	services: [],
	totalServicesPages: 0,
	servicePageNumber: 0,
	isLoading: false
}

/**
 * DashboardCardLists component
 */
const DashboardCardLists = (props) => {
	const { dashboard } = props

	return (
		<Fragment>
			<DashboardCard
				mainColor={defaultTheme.palette.serviceStatus.completedServiceMain}
				secondaryColor={
					defaultTheme.palette.serviceStatus.completedServiceDark
				}
				totalServices={dashboard ? dashboard.totalCompleted : 0}
				percentajeValue={
					dashboard ? dashboard.percentageTotalCompleted : 0
				}
				label={'Completed Services'}
			/>
			<DashboardCard
				mainColor={defaultTheme.palette.serviceStatus.replacedServiceMain}
				secondaryColor={defaultTheme.palette.serviceStatus.replacedServiceDark}
				totalServices={dashboard ? dashboard.totalReplaced : 0}
				percentajeValue={
					dashboard ? dashboard.percentageTotalReplaced : 0
				}
				label={'Replaced Services'}
			/>
			<DashboardCard
				mainColor={defaultTheme.palette.serviceStatus.cancelServiceMain}
				secondaryColor={
					defaultTheme.palette.serviceStatus.cancelServiceDark
				}
				totalServices={dashboard ? dashboard.totalCancelled : 0}
				percentajeValue={
					dashboard ? dashboard.percentageTotalCancelled : 0
				}
				label={'Cancelled  Services'}
			/>
            <DashboardCard
				mainColor={defaultTheme.palette.primary.light}
				secondaryColor={
					defaultTheme.palette.primary.main
				}
				totalServices={dashboard ? dashboard.totalServices : 0}
				percentajeValue={
					dashboard ? dashboard.percentageTotal : 0
				}
				label={'Total  Services'}
			/>
		</Fragment>
	)
}

export default withImmutablePropsToJS(IndexClosedDesktopView)
